import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faFilePdf, faImage, faLock } from '@fortawesome/pro-solid-svg-icons';
import { useApplicationContext } from 'ApplicationContext';
import { settingsApi } from 'Pages/Admin/Settings/settingsApi';

export type AttachmentKind = 'Image' | 'Pdf' | 'Email';

export interface AttachmentDto {
    id: string;
    fileName: string;
    mediaType: string;
    kind: AttachmentKind;
    sizeBytes: number;
    /** Gehört die Datei inzwischen zur Kalkulation? Dann überlebt sie das Gespräch. */
    belongsToProject: boolean;
    createdAt: string;
}

const ICONS: Record<AttachmentKind, typeof faImage> = {
    Image: faImage,
    Pdf: faFilePdf,
    Email: faEnvelope,
};

const readable = (bytes: number) =>
    bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;

/**
 * Die Unterlagen eines Gesprächs, oben am Rand.
 *
 * **Warum am oberen Rand und nicht im Verlauf.** Eine Datei ist kein Gesprächsbeitrag, sondern
 * Zustand: sie liegt für die Dauer des Gesprächs dabei und wirkt auf jede Antwort. Als Blase im
 * Verlauf wäre sie nach zehn Zügen aus dem Blick, obwohl sie weiter mitwirkt — und niemand wüsste
 * mehr, was der Assistent eigentlich vor sich hat.
 *
 * **Warum das Schloss.** Nach dem Übernehmen in den Kalkulator gehört die Datei zur Kalkulation und
 * überlebt das Gespräch. Das ist ein Unterschied, den man sehen muss: alles andere hier wird beim
 * Verwerfen gelöscht.
 */
export const AttachmentBar: React.FC<{
    draftId: string;
    /** Von außen angestoßene Aktualisierung, etwa nach einem Ablegen auf dem Panel. */
    reloadToken?: number;
}> = ({ draftId, reloadToken }) => {
    const theme = useTheme();
    const context = useApplicationContext();
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const [items, setItems] = useState<AttachmentDto[]>([]);
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const r = await http.get<AttachmentDto[]>(`/AiDraft/${draftId}/attachments`);
            setItems(r.data);
        } catch {
            /* Eine nicht ladbare Liste darf das Gespräch nicht aufhalten. */
        }
    }, [http, draftId]);

    useEffect(() => { load(); }, [load, reloadToken]);

    const remove = async (item: AttachmentDto) => {
        setBusy(item.id);
        setError(null);
        try {
            await http.delete(`/AiDraft/${draftId}/attachment/${item.id}`);
            setItems((cur) => cur.filter((i) => i.id !== item.id));
        } catch {
            setError(`${item.fileName} konnte nicht entfernt werden.`);
        } finally {
            setBusy(null);
        }
    };

    if (items.length === 0 && !error) return null;

    return (
        <Box
            sx={{
                flexShrink: 0, px: 2, py: 1,
                borderBottom: 1, borderColor: 'divider',
                bgcolor: alpha(theme.palette.primary.main, 0.03),
            }}
        >
            {error && <Alert severity="warning" sx={{ mb: 1, py: 0 }}>{error}</Alert>}

            <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>
                    Unterlagen
                </Typography>

                {items.map((item) => (
                    <Tooltip
                        key={item.id}
                        title={item.belongsToProject
                            ? `${item.fileName} — gehört zur Kalkulation und bleibt erhalten`
                            : `${item.fileName} — wird mit dem Gespräch gelöscht`}
                    >
                        <Chip
                            size="small"
                            variant="outlined"
                            icon={busy === item.id
                                ? <CircularProgress size={12} sx={{ ml: 1 }} />
                                : <FontAwesomeIcon icon={ICONS[item.kind]} style={{ marginLeft: 8 }} />}
                            label={(
                                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                                    <Box component="span" sx={{ maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {item.fileName}
                                    </Box>
                                    <Box component="span" sx={{ color: 'text.disabled', fontSize: 10 }}>
                                        {readable(item.sizeBytes)}
                                    </Box>
                                    {item.belongsToProject && (
                                        <FontAwesomeIcon icon={faLock} fontSize={9} />
                                    )}
                                </Stack>
                            )}
                            onDelete={busy === item.id ? undefined : () => remove(item)}
                            sx={{ maxWidth: 300 }}
                        />
                    </Tooltip>
                ))}
            </Stack>
        </Box>
    );
};

export default AttachmentBar;
